import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { getQuantityTypeList } from "../sharedCode/queries";
import { QuantityType } from "../sharedCode/avroGraphql";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";

export default function useQuantityTypeBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getQuantityTypeList, {
        fetchPolicy: "cache-and-network",
    });

    const fetchData = (firstRun: boolean) => {
        if (firstRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = results.data.getQuantityTypes.slice() as QuantityType[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: QuantityType[]) {
        TicketDatabase.transaction("rw", TicketDatabase.quantityType, async () => {
            await TicketDatabase.quantityType
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.quantityType.bulkDelete(keys);
                })
                .then(() => TicketDatabase.quantityType.bulkAdd(rawList));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = ObjectUtils.convertToUppercase(gqlResult.getQuantityTypes.slice()) as QuantityType[];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.quantityType.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
